import React, { useState } from "react";
import { motion } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import logo from "../assets/logo.png";
import background from "../assets/background.jpg";
import {
  FaUser,
  FaLock,
  FaEye,
  FaEyeSlash,
  FaUniversalAccess,
  FaFileAlt,
  FaLifeRing,
  
} from "react-icons/fa";
import { CheckCircle, Frown } from "lucide-react";
import Footer from "../components/Footer";
// import SimpleHeader from "../assets/SimpleHeader";

const InputField = ({
  type,
  placeholder,
  icon,
  value,
  onChange,
  ariaLabel,
  error,
}) => {
  const [isPasswordShown, setIsPasswordShown] = useState(false);
  const isPassword = type === "password";
  const inputType = isPassword && isPasswordShown ? "text" : type;

  const renderIcon = () => {
    switch (icon) {
      case "person":
        return <FaUser className="text-blue-500 w-4 h-4" />;
      case "lock":
        return <FaLock className="text-blue-500 w-4 h-4" />;
      default:
        return null;
    }
  };

  return (
    <div className="relative w-full mb-3">
      <div className="absolute left-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
        {renderIcon()}
      </div>

      <input
        type={inputType}
        placeholder={placeholder}
        required
        value={value}
        onChange={onChange}
        aria-label={ariaLabel}
        aria-invalid={!!error}
        className={`w-full h-10 pl-10 pr-10 rounded border ${
          error ? "border-red-500" : "border-gray-300"
        } text-sm text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-1 ${
          error ? "focus:ring-red-500" : "focus:ring-blue-500"
        } bg-white transition-all duration-200 shadow-sm`}
        autoComplete={
          ariaLabel.toLowerCase() === "password"
            ? "current-password"
            : "username"
        }
      />

      {isPassword && (
        <button
          type="button"
          onClick={() => setIsPasswordShown((prev) => !prev)}
          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 rounded"
          aria-label={isPasswordShown ? "Hide password" : "Show password"}
        >
          {isPasswordShown ? <FaEyeSlash /> : <FaEye />}
        </button>
      )}
    </div>
  );
};

const Login = () => {
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showWelcome, setShowWelcome] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!username || !password) {
      setError("Please fill in both fields.");
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/login`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ username, password }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        setError(
          data.message || "Login failed. Please check your credentials."
        );
        return;
      }

      // Save user and token to localStorage
      const sessionId = Date.now() + "_" + Math.random();
      localStorage.setItem("sessionId", sessionId);
      localStorage.setItem("user", JSON.stringify(data.user));
      localStorage.setItem("token", data.token);

      if (data.redirectTo) {
        setShowWelcome(true);
        setTimeout(() => {
          navigate("/dashboard");
        }, 1500);
      } else {
        setError("Invalid redirect path from server.");
      }
    } catch (err) {
      console.error("Login error:", err);
      setError("Network error. Please check your connection and try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex flex-col bg-cover bg-center bg-no-repeat"
      style={{
        backgroundImage: `url(${background})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      }}
    >
      {/* Professional Header */}
      {/* <SimpleHeader onLogoClick={() => window.scrollTo(0, 0)} /> */}

      {/* Main content - grows to fill space */}
      <div className="flex-1 flex items-center justify-center px-4 py-6">
      {showWelcome ? (
        <motion.div
          key="welcome-screen"
          className="flex flex-col items-center justify-center bg-white/90 p-6 rounded-lg shadow-lg border border-gray-200 text-center max-w-xs w-full"
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ type: "spring", duration: 0.5 }}
        >
          <motion.div
            className="text-green-500 mb-3"
            initial={{ scale: 0 }}
            animate={{ scale: [0, 1.2, 1] }}
            transition={{ duration: 0.4, ease: "easeOut" }}
          >
            <CheckCircle size={48} strokeWidth={1.5} />
          </motion.div>
          <motion.h2
            className="text-lg font-semibold text-gray-800 mb-2"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            Welcome back!
          </motion.h2>
          <div className="flex flex-col items-center justify-center space-y-2">
            <motion.div
              className="relative w-8 h-8"
              animate={{ rotate: 360 }}
              transition={{
                repeat: Infinity,
                duration: 1,
                ease: "linear",
              }}
            >
              <div className="absolute inset-0 rounded-full border-3 border-blue-500 border-t-transparent"></div>
            </motion.div>
            <motion.p
              className="text-sm text-gray-600"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4 }}
            >
              Redirecting...
            </motion.p>
          </div>
        </motion.div>
      ) : (
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 0.4, ease: "easeOut" }}
          className="backdrop-blur-sm bg-white/80 p-6 max-w-xs w-full rounded-lg shadow-lg border border-gray-200"
        >
          <motion.img
            src={logo}
            alt="University Logo"
            className="mx-auto mb-4 w-20"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          />

          <h1 className="text-lg font-semibold text-center text-gray-800 mb-4">
            Welcome Back
          </h1>

          <form onSubmit={handleSubmit} noValidate>
            <InputField
              type="text"
              placeholder="Username"
              icon="person"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              ariaLabel="Username"
              error={error && !password}
            />
            <InputField
              type="password"
              placeholder="Password"
              icon="lock"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              ariaLabel="Password"
              error={error && !username}
            />

            {error && (
              <motion.div
                role="alert"
                aria-live="assertive"
                className="flex items-start gap-2 bg-red-50 border-l-3 border-red-500 text-red-800 px-3 py-2 rounded text-xs mb-3"
                initial={{ opacity: 0, y: -5 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.2 }}
              >
                <Frown className="flex-shrink-0 mt-0.5 text-red-500 w-3 h-3" />
                <p className="text-xs">{error}</p>
              </motion.div>
            )}

            <div className="text-right mb-3">
              <Link
                to="/forgot-password"
                className="text-xs text-blue-600 hover:underline"
                aria-label="Forgot password?"
              >
                Forgot password?
              </Link>
            </div>

            <motion.button
              type="submit"
              disabled={isLoading}
              className={`w-full ${
                isLoading ? "bg-blue-400" : "bg-blue-600"
              } text-white text-sm font-medium py-2 rounded shadow hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-400 transition duration-200 flex justify-center items-center`}
              whileHover={!isLoading ? { scale: 1.02 } : {}}
              whileTap={!isLoading ? { scale: 0.98 } : {}}
            >
              {isLoading ? (
                <>
                  <svg
                    className="animate-spin -ml-1 mr-2 h-3 w-3 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Logging in...
                </>
              ) : (
                "Log In"
              )}
            </motion.button>

            <p className="mt-3 text-center text-xs text-gray-600">
              By continuing, you agree to our{" "}
              <Link
                to="#"
                className="text-blue-600 hover:underline"
                aria-label="Terms & Conditions"
              >
                Terms
              </Link>{" "}
              and{" "}
              <Link
                to="#"
                className="text-blue-600 hover:underline"
                aria-label="Privacy Policy"
              >
                Privacy
              </Link>
              .
            </p>
          </form>

          <motion.p
            className="text-center text-xs font-medium mt-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
          >
            No account?{" "}
            <Link
              to="/registration"
              className="text-blue-600 hover:underline"
              aria-label="Register Here"
            >
              Register
            </Link>
          </motion.p>
        </motion.div>
      )}
      </div>

      {/* Footer sits at bottom */}
      <Footer />
    </div>
  );
};

export default Login;